import { sideBar, updateDialog } from './Sidebar.style';
import { setSearchCondition } from '~/store/resource';
import { setSearchCondition as setInterSearchCondition } from '~/store/internetResource';
import { useSelector } from 'react-redux';
import { useAppDispatch } from '~/store';
import { SIDEBAR, UpdateElectronStatus } from '~/types/enum';
import { Dispatch, SetStateAction, useEffect, useMemo, useState } from 'react';
import { getterClientResourceFilter } from '~/store/resource/resourceGetter';
import { ResourceSearchKeyType } from '~/api/oneBoxV3/oneBoxV3.type';
import downloadNani from '/assets/image/downloadNani.svg';
import downloadWindows from '/assets/image/downloadWindows.svg';
import {
  Box,
  CircularProgress,
  IconButton,
  Stack,
  Typography,
} from '@mui/material';
import { setLocalSotrage } from '~/components/Global/ResourceSearch';
import { isWeb } from '~/utils/helper/device';
import { useUpdate } from '~/utils/hook/useUpdate';
import { invokeChannel, resolveInternetPageSize } from '~/utils/constants';
import { pxToRem } from '~/theme/typography';
import { BaseDialog } from '~/components/Global/Dialog/BaseDialog';
import {
  BASE_BUTTON_COLOR,
  BASE_BUTTON_VARIANT,
  BaseButton,
} from '~/components/Global/Button';
import { useCommon } from '~/utils/hook/useCommon';
import CloseIcon from '@mui/icons-material/Close';
import { resourceCategoryEvent } from '~/tracking';

// 載速配入口先只在 dev / uat 開放，正式站關閉。只認字串 'true'：環境檔漏設這個值時
// 一律不顯示，避免沒準備好的入口跟著上到正式站。
const showNaniDownload = import.meta.env.VITE_FEATURE_NANI_DOWNLOAD === 'true';

type TProps = {
  setSidebarRef: Dispatch<SetStateAction<HTMLUListElement | undefined>>;
  sidebarList: string[];
  isFetching: boolean;
};

export const Sidebar = ({ setSidebarRef, sidebarList, isFetching }: TProps) => {
  const { updateHandle, getUpdateMessage, isInstall, isDownload } = useUpdate();
  const { openExternal } = useCommon();

  const [updateState, setUpdateState] = useState<
    keyof typeof UpdateElectronStatus
  >(UpdateElectronStatus.updateAvailable);
  const [updateElectronModal, setUpdateElectronModal] = useState(false);
  const [showUpdateDialog, setShowUpdateDialog] = useState(true);
  const [electronVersion, setElectronVersion] = useState('');
  const { resources1, eduCodes, years, grades, subjects } = useSelector(
    getterClientResourceFilter,
  );

  const dispatch = useAppDispatch();

  // setSelected 純函數，只負責回傳 boolean
  const setSelected = (col: string, res1: string) => {
    if (!sidebarList.includes(res1)) {
      return sidebarList[0] === col;
    }
    return resources1 === col;
  };

  const updateLocalStorage = (res1: string) => {
    let resourceSearchKey: ResourceSearchKeyType = {};
    const storedValue = localStorage.getItem('resourceSearchKey');
    if (storedValue) {
      resourceSearchKey = JSON.parse(storedValue) as ResourceSearchKeyType;
    }
    const combinedSearchKey = {
      ...resourceSearchKey,
      resources1: res1,
    };
    setLocalSotrage(combinedSearchKey);
  };

  useEffect(() => {
    if (isWeb) return;
    getUpdateMessage(updateMessage => {
      if (!updateMessage) return;
      setUpdateState(updateMessage.state);

      if (updateMessage.state === UpdateElectronStatus.updateAvailable) {
        isDownload();
      }

      if (updateMessage.state === UpdateElectronStatus.updateNotAvailable) {
        setTimeout(() => {
          setShowUpdateDialog(false);
        }, 5000);
      }
    });
    // updateDownloadProgress(progress => {
    //   if (!progress) return;
    //   setUpdateProgress(progress.percent);
    // });
    updateHandle();
  }, []);

  useEffect(() => {
    if (isWeb) return;
    const getVersion = async () => {
      try {
        const version = (await window.api.invoke(
          invokeChannel.getVersion,
        )) as string;
        setElectronVersion(version || import.meta.env.VITE_APP_VERSION);
      } catch {
        // 舊版 Electron 外殼未註冊 getVersion handler（shell 版本落後於 web app），
        // invoke 會 reject。退回前端內建版本號，避免未處理 rejection 誤報 Sentry。
        setElectronVersion(import.meta.env.VITE_APP_VERSION);
      }
    };
    void getVersion();
  }, []);

  const updateText = useMemo(() => {
    switch (updateState) {
      case UpdateElectronStatus.checking:
      case UpdateElectronStatus.updateAvailable:
      case UpdateElectronStatus.downloading:
        return (
          <updateDialog.SubTextWithLoading>
            檢查版本中
          </updateDialog.SubTextWithLoading>
        );
      // case UpdateElectronStatus.downloadCompleted:
      case UpdateElectronStatus.updated:
        return (
          <BaseButton
            variant={BASE_BUTTON_VARIANT.FILLED}
            color={BASE_BUTTON_COLOR.SECONDARY}
            text="點擊更新"
            width="100%"
            style={{ height: '32px' }}
            onClick={() => setUpdateElectronModal(true)}
          />
        );
      case UpdateElectronStatus.updateNotAvailable:
        return <updateDialog.SubText>已是最新版本</updateDialog.SubText>;
      case UpdateElectronStatus.error:
        return (
          <>
            <updateDialog.SubText sx={{ color: '#FCE2CE' }}>
              更新錯誤請下載安裝檔
            </updateDialog.SubText>
            <BaseButton
              variant={BASE_BUTTON_VARIANT.GHOST}
              color={BASE_BUTTON_COLOR.ERROR}
              text="前往下載"
              width="100%"
              style={{
                height: '32px',
                marginTop: '4px',
              }}
              onClick={() => openExternal('https://box3.nani.com.tw')}
            />
          </>
        );
      default:
        return '';
    }
  }, [updateState]);

  const updateElectronModalAction = (
    <>
      <BaseButton
        variant={BASE_BUTTON_VARIANT.OUTLINED}
        color={BASE_BUTTON_COLOR.PRIMARY}
        onClick={() => setUpdateElectronModal(false)}
        text="下次再說"
      />
      <BaseButton
        variant={BASE_BUTTON_VARIANT.FILLED}
        color={BASE_BUTTON_COLOR.PRIMARY}
        onClick={() => isInstall()}
        text="安裝"
      />
    </>
  );

  return (
    <sideBar.List
      ref={ref => setSidebarRef(ref as HTMLUListElement)}
      id="sidebar"
    >
      {isWeb && (
        <sideBar.DownloadSection>
          {showNaniDownload && (
            <sideBar.DownloadLink
              href={`${import.meta.env.VITE_NANI_DOWNLOAD_URL}/Nani載速配 Setup.exe`}
              target="_blank"
              rel="noreferrer"
            >
              <sideBar.DownloadImage
                src={downloadNani}
                alt="下載 Nani 載速配"
              />
            </sideBar.DownloadLink>
          )}
          <sideBar.DownloadLink
            href={`${import.meta.env.VITE_WINDOWS_DOWNLOAD_URL}/NaniBox3 Setup.exe`}
            target="_blank"
            rel="noreferrer"
          >
            <sideBar.DownloadImage
              src={downloadWindows}
              alt="下載 Windows 電腦版"
            />
          </sideBar.DownloadLink>
        </sideBar.DownloadSection>
      )}
      {!isFetching ? (
        <>
          {sidebarList.map(col => {
            const displayText = col;
            const size =
              (col as SIDEBAR) === SIDEBAR.latestNews
                ? 5
                : col &&
                    (col as SIDEBAR) !== SIDEBAR.ebook &&
                    (col as SIDEBAR) !== SIDEBAR.tiku
                  ? (col as SIDEBAR) === SIDEBAR.internet
                    ? 100
                    : 4
                  : 30;
            return (
              <sideBar.ListButton
                key={col}
                selected={setSelected(col, resources1 || '')}
                disableRipple
                onClick={() => {
                  updateLocalStorage(col);
                  dispatch(
                    setSearchCondition({
                      resources1: col,
                      pageSize: size,
                      page: 0,
                    }),
                  );
                  // 與畫面上學制／學年度／科目／年級一致（internetResource 另存一份，供網路資源 API）
                  dispatch(
                    setInterSearchCondition({
                      resources1: col,
                      pageSize: resolveInternetPageSize(col),
                      page: 0,
                      eduCodes: eduCodes ?? 'all',
                      years: years ?? 'all',
                      subjects: subjects ?? 'all',
                      grades: grades ?? 'all',
                    }),
                  );
                  resourceCategoryEvent({
                    category: col,
                    edu: eduCodes ?? undefined,
                    subject: subjects ?? undefined,
                    year: years ?? undefined,
                    grade: grades ?? undefined,
                  });
                }}
              >
                <span>{displayText}</span>
              </sideBar.ListButton>
            );
          })}
          {/* 以下 Dialog 僅為預留空間使用，使用者看不到 */}
          {!isWeb && showUpdateDialog && (
            <updateDialog.DialogPlaceholder>
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                }}
              >
                <Typography sx={{ fontWeight: '600', fontSize: pxToRem(18) }}>
                  {`系統版本 v${electronVersion}`}
                </Typography>
                {updateState === UpdateElectronStatus.error && (
                  <IconButton
                    sx={{ padding: 0, color: 'white' }}
                    onClick={() => setShowUpdateDialog(false)}
                  >
                    <CloseIcon />
                  </IconButton>
                )}
              </Box>
              {updateText}
            </updateDialog.DialogPlaceholder>
          )}
        </>
      ) : (
        <Stack
          justifyContent="center"
          alignItems="center"
          sx={{ height: '100%' }}
        >
          <CircularProgress sx={{ color: 'white' }} />
          <sideBar.LoadingMessage>載入中...</sideBar.LoadingMessage>
        </Stack>
      )}
      {!isWeb && showUpdateDialog && (
        <updateDialog.Dialog>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            <Typography sx={{ fontWeight: '600', fontSize: pxToRem(18) }}>
              {`系統版本 v${electronVersion}`}
            </Typography>
            {updateState === UpdateElectronStatus.error && (
              <IconButton
                sx={{ padding: 0, color: 'white' }}
                onClick={() => setShowUpdateDialog(false)}
              >
                <CloseIcon />
              </IconButton>
            )}
          </Box>
          {updateText}
        </updateDialog.Dialog>
      )}
      <BaseDialog
        open={updateElectronModal}
        handleClose={() => setUpdateElectronModal(false)}
        action={updateElectronModalAction}
        title="更新 NaniBox 3.0"
      >
        <Typography
          variant="body1"
          sx={{ maxWidth: '600px', minWidth: '500px' }}
        >
          偵測到最新版本可供下載，是否現在下載並安裝？或至 NaniBox 3.0
          網頁下載「Windows 電腦版」程式並安裝。NaniBox 3.0 網址：
          https://box3.nani.com.tw
        </Typography>
      </BaseDialog>
    </sideBar.List>
  );
};
